// SPDX-FileCopyrightText: Copyright 2023 Dash0 Inc.

import { cva, VariantProps } from "class-variance-authority";
import debounce from "lodash-es/debounce";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { namespacedForwardRef } from "@/lib/utils/react/namespaced-forward-ref";

export const inputVariants = cva(
	"flex py-0 w-full rounded-md border border-input bg-input text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-subtle placeholder:italic focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-(--input-color-bg) disabled:cursor-not-allowed disabled:opacity-50",
	{
		variants: {
			size: {
				xs: "h-6 text-xs px-2",
				sm: "h-8 text-sm px-2",
				md: "h-10 text-sm px-3",
			},
			variant: {
				inline: "inline-block h-6 px-2 py-0.5 text-xs",
			},
			hasError: {
				true: "border-input-error focus-visible:ring-critical",
			},
		},
		defaultVariants: {
			size: "md",
			hasError: false,
		},
		compoundVariants: [
			{
				variant: "inline",
				size: "xs",
				className: "w-14 text-center",
			},
			{
				variant: "inline",
				size: "sm",
				className: "w-20",
			},
		],
	}
);

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & VariantProps<typeof inputVariants>;

const Input = namespacedForwardRef<
	HTMLInputElement,
	InputProps,
	{
		Debounced: typeof DebouncedInput;
	}
>(({ className, size, type, variant, readOnly, hasError, autoComplete = "off", ...props }, ref) => {
	// Custom variant for inline inputs. When in read only we want to display the value as text not an input.
	const autoCompleteAttrs = autoCompleteAttributes(autoComplete);

	if (variant === "inline") {
		return (
			<div className="inline-flex flex-row gap-1">
				{readOnly ? (
					<div
						className={cn(
							inputVariants({ size, hasError, variant }),
							{
								"h-auto w-auto cursor-default border-transparent p-0": readOnly,
							},
							"readonly-input",
							className
						)}
					>
						{props.value ?? props.placeholder}
					</div>
				) : (
					<input
						type={type}
						className={cn(inputVariants({ size, hasError, variant }), className)}
						ref={ref}
						readOnly={readOnly}
						{...autoCompleteAttrs}
						{...props}
					/>
				)}
			</div>
		);
	}

	return (
		<input
			type={type}
			className={cn(
				inputVariants({ size, hasError, variant }),
				{
					"cursor-default border-transparent": readOnly,
				},
				className
			)}
			ref={ref}
			readOnly={readOnly}
			{...autoCompleteAttrs}
			{...props}
		/>
	);
});

type HTMLInputAutoCompleteAttribute = AutoFill | (string & {});

interface AutoCompleteAttributes {
	/*
	 * Default for browsers, but also should be respected by:
	 * - BitWarden: https://github.com/bitwarden/clients/blob/69d42a73e38822d3bed53a08cdd09457b9b7a65d/apps/browser/src/autofill/services/collect-autofill-content.service.ts#L451
	 * - Enpass: https://support.enpass.io/docs/manual-desktop/browser_ext_using.html
	 */
	autoComplete: HTMLInputAutoCompleteAttribute;
	// 1Password: https://developer.1password.com/docs/web/compatible-website-design/
	"data-1p-ignore": true | null;
	// LastPass: https://support.lastpass.com/s/document-item?language=en_US&bundleId=lastpass&topicId=LastPass/c_lp_prevent_fields_from_being_filled_automatically.html&_LANG=enus
	"data-lpignore": true | null;
	// Dashlane: https://support.dashlane.com/hc/en-us/articles/4420122792594-Optimize-your-web-forms-for-Dashlane-Autofill
	"data-form-type": "other" | null;
}

export function autoCompleteAttributes(autoComplete: HTMLInputAutoCompleteAttribute): AutoCompleteAttributes {
	if (autoComplete === "off") {
		return {
			autoComplete,
			"data-1p-ignore": true,
			"data-lpignore": true,
			"data-form-type": "other",
		};
	}

	return {
		autoComplete,
		"data-1p-ignore": null,
		"data-lpignore": null,
		"data-form-type": null,
	};
}

Input.displayName = "Input";

type DebouncedInputProps = {
	handleChange: (value: InputProps["value"]) => void;
} & Omit<InputProps, "onChange">;

/**
 * Input field where the value is updated locally but not sent to the parent until the user stops typing.
 */
const DebouncedInput = ({ handleChange, onBlur, value, ...rest }: DebouncedInputProps) => {
	// debounced series name field input as a temporary solution to slow big tables
	const [inputValue, setInputValue] = useState(value);
	const lastValue = useRef(value);

	const debouncedUpdater = useMemo(() => {
		return debounce(
			(value: InputProps["value"]) => {
				handleChange(value);
				lastValue.current = value;
			},
			500,
			{
				leading: false,
				trailing: true,
			}
		);
	}, [handleChange]);

	useEffect(() => {
		// we're accounting for the fact that we use tanstack quite often, and that if the value is the same, the form object will
		// still change pointers :(
		if (inputValue !== lastValue.current) {
			debouncedUpdater(inputValue);
		}

		return () => {
			debouncedUpdater.cancel();
		};
	}, [debouncedUpdater, inputValue]);

	return <Input {...rest} value={inputValue} onChange={(e) => setInputValue(e.target.value)} />;
};

Input.Debounced = DebouncedInput;

export { Input };
